import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { SchemaService } from '../auxiliary-data/schema-service/schema.service';
import { FieldHandler, ObjectData, ObjectSchema } from './auxiliary-data-table/auxiliary-data-table.interface';
import { FieldDataService } from './field-data-service/field-data.service';
import { chooseHandler } from './renderer-utils';
import { ObjectType } from './schema-service/schema-interface';

export enum LoadingStatus {
  LOADING = 1,
  OK = 2,
  EMPTY = 3,
  ERROR = 4,
}

export enum SavingStatus {
  SAVING = 1,
  OK = 2,
  ERROR = 3,
}

@Injectable()
export class AuxiliaryFieldHandlerService {
  LoadingStatus = LoadingStatus;

  private partnerId!: string;
  private objectType!: ObjectType;
  private objectId!: string;

  private loadingStatus$$ = new ReplaySubject<LoadingStatus>(1);
  loadingStatus$ = this.loadingStatus$$.asObservable();

  private savingStatus$$ = new ReplaySubject<SavingStatus>(1);
  savingStatus$ = this.savingStatus$$.asObservable();

  fieldHandlers$!: Observable<FieldHandler[]>;

  constructor(
    private readonly fieldDataService: FieldDataService,
    private readonly schemaService: SchemaService,
  ) {}

  init(partnerId: string, objectType: ObjectType, objectId: string, showLabel: boolean, readOnly = false): void {
    this.partnerId = partnerId;
    this.objectType = objectType;
    this.objectId = objectId;

    this.fieldHandlers$ = this.buildFieldData$(showLabel, readOnly).pipe(
      map((handlers) => handlers.filter((h) => h !== null)),
    );
  }

  private buildFieldData$(showLabel: boolean, readOnly = false): Observable<FieldHandler[]> {
    const listData$ = this.fieldDataService.listData(this.partnerId, this.objectType, this.objectId, { pageSize: 100 });
    const listSchemas$ = this.schemaService.listFieldSchemas(this.objectType, this.partnerId, false, '', 100);

    this.loadingStatus$$.next(LoadingStatus.LOADING);
    return combineLatest([listData$, listSchemas$]).pipe(
      take(1),
      catchError((err) => {
        this.loadingStatus$$.next(LoadingStatus.ERROR);
        throw err;
      }),
      map(([fullData, response]) => {
        const objectSchema = JSON.parse(fullData.jsonSchema) as ObjectSchema;
        const objectData = JSON.parse(fullData.jsonData) as ObjectData;
        return (response.fieldSchemas || []).map((fieldSchema) => {
          return chooseHandler(
            fieldSchema.fieldId,
            objectSchema.properties[fieldSchema.fieldId],
            objectData[fieldSchema.fieldId],
            showLabel,
            [],
            readOnly,
          );
        });
      }),
      tap((handlers) => this.loadingStatus$$.next(handlers.length ? LoadingStatus.OK : LoadingStatus.EMPTY)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  saveChanges(): Observable<boolean> {
    return of(null).pipe(
      tap(() => this.savingStatus$$.next(SavingStatus.SAVING)),
      switchMap(() => this.fieldHandlers$),
      map((fieldHandlers) =>
        fieldHandlers.filter((handler) => handler.hasModification()).map((handler) => handler.upsertOperation()),
      ),
      switchMap((fieldDataUpsertOperations) => {
        if (!fieldDataUpsertOperations?.length) {
          return of(false);
        }
        return this.fieldDataService
          .upsertData(this.partnerId, this.objectType, this.objectId, fieldDataUpsertOperations)
          .pipe(map(() => true));
      }),
      catchError((err) => {
        this.savingStatus$$.next(SavingStatus.ERROR);
        throw err;
      }),
      tap(() => this.savingStatus$$.next(SavingStatus.OK)),
    );
  }
}
